'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import instance from '@/lib/axiosInstance'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { AxiosError } from 'axios'
import Image from 'next/image'

interface Page {
  id: string
  title: string
  description: string
  slug: string
  content: string
  created_at: string
  updated_at: string
}

interface PageClientProps {
  initialPage: Page | null
  pageId: string
  previousPage: Page | null
  nextPage: Page | null
}

export default function PageClient({ initialPage, pageId, previousPage, nextPage }: PageClientProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const [page, setPage] = useState<Page | null>(initialPage)
  const [previousPageData, setPreviousPageData] = useState<Page | null>(previousPage)
  const [nextPageData, setNextPageData] = useState<Page | null>(nextPage)
  const [isLoading, setIsLoading] = useState(!initialPage)
  const [error, setError] = useState<string | null>(null)

  console.log(previousPage, nextPageData)

  useEffect(() => {
    // Only fetch if we don't have initial data
    if (!initialPage) {
      const fetchPage = async () => {
        try {
          setIsLoading(true)
          const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/pages/${pageId}`)
          const data = await response.json()
          if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch page')
          }
          setPage(data.page)
          setPreviousPageData(data.previous)
          setNextPageData(data.next)
        } catch (error: unknown) {
          console.error('Error fetching page:', error)
          if ((error as AxiosError).response?.status === 404) {
            setError('Page not found')
          } else {
            setError('Failed to load page')
            toast.error(t('common.error.title'), {
              description: 'Failed to load page content',
            })
          }
        } finally {
          setIsLoading(false)
        }
      }

      fetchPage()
    }
  }, [initialPage, pageId, t])

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="space-y-4">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-muted-foreground mb-2">
            {error === 'Page not found' ? 'Page Not Found' : 'Error Loading Page'}
          </h1>
          <p className="text-muted-foreground">
            {error === 'Page not found'
              ? 'The page you are looking for does not exist.'
              : 'There was an error loading the page content.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 scroll-smooth transition-colors duration-300 relative overflow-hidden">
      {/* Natural background with subtle gradient */}
      <div className="fixed inset-0 -z-50">
        <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-neutral-50 to-neutral-100 dark:from-stone-900 dark:via-neutral-900 dark:to-neutral-900 transition-colors duration-300" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.01),transparent_70%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.01),transparent_70%)]" />
      </div>

      {/* Navigation Header */}
      <nav className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 w-[95%] max-w-6xl rounded-2xl shadow-xl mx-auto backdrop-blur-3xl bg-white/40 dark:bg-neutral-900/90 border border-stone-200/50 dark:border-neutral-700/50 transition-colors duration-300">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => router.back()}
              className="gap-2 text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-stone-800 dark:bg-transparent text-white shadow-lg transition-colors duration-300">
                <Image
                  src="/fd-logo.svg"
                  alt="FlightDeck Logo"
                  width={20}
                  height={20}
                />
              </div>
              <div className="hidden sm:block">
                <h2 className="font-semibold text-stone-900 dark:text-stone-100 transition-colors duration-300">FlightDeck</h2>
                <p className="text-xs text-stone-600 dark:text-stone-400">Documentation</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">
              {new Date(page.updated_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-28 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Hero Section */}
          <div className="mb-16">
            <div className="relative">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-stone-600/10 via-slate-600/10 to-stone-600/10 rounded-3xl blur-2xl"></div>
              
              <div className="relative backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-xl rounded-2xl p-8 transition-colors duration-300">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-stone-600 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-xl">📚</span>
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h1 className="text-4xl lg:text-5xl font-bold mb-4 text-stone-900 dark:text-stone-100 transition-colors duration-300">
                      {page.title}
                    </h1>
                    {page.description && (
                      <p className="text-xl text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                        {page.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="relative mb-16">
            {/* Content background */}
            <div className="absolute inset-0 backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 shadow-lg rounded-2xl transition-colors duration-300"></div>
            
            <div className="relative p-8 lg:p-12">
              <div
                className="prose prose-stone dark:prose-invert prose-lg max-w-none
                  [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-6 [&_h1]:mt-8 [&_h1]:first:mt-0 [&_h1]:text-stone-900 [&_h1]:dark:text-stone-100 [&_h1]:border-b [&_h1]:border-stone-200/50 [&_h1]:dark:border-stone-700/50 [&_h1]:pb-3
                  [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:text-stone-900 [&_h2]:dark:text-stone-100 [&_h2]:border-b [&_h2]:border-stone-200/30 [&_h2]:dark:border-stone-700/30 [&_h2]:pb-2
                  [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-3 [&_h3]:mt-6 [&_h3]:text-stone-900 [&_h3]:dark:text-stone-100 [&_h3]:border-b [&_h3]:border-stone-200/20 [&_h3]:dark:border-stone-700/20 [&_h3]:pb-1
                  [&_h4]:text-lg [&_h4]:font-medium [&_h4]:mb-3 [&_h4]:mt-5 [&_h4]:text-stone-900 [&_h4]:dark:text-stone-100
                  [&_h5]:text-base [&_h5]:font-medium [&_h5]:mb-2 [&_h5]:mt-4 [&_h5]:text-stone-900 [&_h5]:dark:text-stone-100
                  [&_h6]:text-sm [&_h6]:font-medium [&_h6]:mb-2 [&_h6]:mt-3 [&_h6]:text-stone-900 [&_h6]:dark:text-stone-100
                  [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-stone-700 [&_p]:dark:text-stone-300
                  [&_ul]:mb-6 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-2
                  [&_ol]:mb-6 [&_ol]:ml-6 [&_ol]:list-decimal [&_ol]:space-y-2
                  [&_li]:text-stone-700 [&_li]:dark:text-stone-300 [&_li]:leading-relaxed
                  [&_blockquote]:border-l-4 [&_blockquote]:border-stone-400/30 [&_blockquote]:dark:border-stone-600/30 [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:bg-stone-50/50 [&_blockquote]:dark:bg-stone-800/50 [&_blockquote]:py-4 [&_blockquote]:rounded-r-lg [&_blockquote]:my-6 [&_blockquote]:backdrop-blur-sm
                  [&_code]:bg-stone-100 [&_code]:dark:bg-stone-800 [&_code]:px-2 [&_code]:py-1 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_code]:border [&_code]:border-stone-200/50 [&_code]:dark:border-stone-700/50
                  [&_pre]:bg-stone-100 [&_pre]:dark:bg-stone-800 [&_pre]:p-6 [&_pre]:rounded-xl [&_pre]:overflow-x-auto [&_pre]:border [&_pre]:border-stone-200/50 [&_pre]:dark:border-stone-700/50 [&_pre]:shadow-sm [&_pre]:backdrop-blur-sm
                  [&_strong]:font-semibold [&_strong]:text-stone-900 [&_strong]:dark:text-stone-100
                  [&_em]:italic [&_em]:text-stone-700 [&_em]:dark:text-stone-300
                  [&_hr]:my-12 [&_hr]:border-stone-200/50 [&_hr]:dark:border-stone-700/50
                  [&_table]:border-collapse [&_table]:border [&_table]:border-stone-200/50 [&_table]:dark:border-stone-700/50 [&_table]:rounded-lg [&_table]:overflow-hidden [&_table]:shadow-sm [&_table]:backdrop-blur-sm [&_table]:bg-white/30 [&_table]:dark:bg-neutral-800/30
                  [&_th]:border [&_th]:border-stone-200/30 [&_th]:dark:border-stone-700/30 [&_th]:px-4 [&_th]:py-3 [&_th]:bg-stone-50/80 [&_th]:dark:bg-stone-800/80 [&_th]:font-semibold [&_th]:text-left [&_th]:text-stone-900 [&_th]:dark:text-stone-100
                  [&_td]:border [&_td]:border-stone-200/30 [&_td]:dark:border-stone-700/30 [&_td]:px-4 [&_td]:py-3 [&_td]:text-stone-700 [&_td]:dark:text-stone-300
                  [&_h1]:scroll-mt-28 [&_h2]:scroll-mt-28 [&_h3]:scroll-mt-28 [&_h4]:scroll-mt-28 [&_h5]:scroll-mt-28 [&_h6]:scroll-mt-28
                external-link-icon [&_a]:text-blue-700 [&_a]:dark:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-900 [&_a]:dark:hover:text-blue-300 [&_a]:transition-colors [&_a]:cursor-pointer [&_a]:decoration-blue-400 [&_a]:dark:decoration-blue-600 [&_a]:underline-offset-2"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
            </div>
          </div>

          {/* Navigation Section */}
          {(previousPageData || nextPageData) && (
            <div className="mb-16">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Previous Page */}
                <div className="order-1">
                  {previousPageData ? (
                    <Link href={`/pages/${previousPageData.slug}`}>
                      <div className="group backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-stone-100/80 dark:bg-stone-800/80 text-stone-600 dark:text-stone-400 group-hover:bg-stone-200/80 dark:group-hover:bg-stone-700/80 group-hover:text-stone-800 dark:group-hover:text-stone-200 transition-all duration-300">
                            <ChevronLeft className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-stone-500 dark:text-stone-500 mb-1">Previous</div>
                            <div className="font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 truncate">
                              {previousPageData.title}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="h-20"></div>
                  )}
                </div>

                {/* Next Page */}
                <div className="order-2">
                  {nextPageData ? (
                    <Link href={`/pages/${nextPageData.slug}`}>
                      <div className="group backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                        <div className="flex items-center gap-4 text-right">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-stone-500 dark:text-stone-500 mb-1">Next</div>
                            <div className="font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 truncate">
                              {nextPageData.title}
                            </div>
                          </div>
                          <div className="p-2 rounded-lg bg-stone-100/80 dark:bg-stone-800/80 text-stone-600 dark:text-stone-400 group-hover:bg-stone-200/80 dark:group-hover:bg-stone-700/80 group-hover:text-stone-800 dark:group-hover:text-stone-200 transition-all duration-300">
                            <ChevronRight className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <div className="h-20"></div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <footer className="border-t border-stone-200/50 dark:border-stone-700/50 pt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-stone-600 to-slate-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">FD</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-900 dark:text-stone-100">FlightDeck Documentation</p>
                  <p className="text-xs text-stone-600 dark:text-stone-400">Powered by FlightDeck</p>
                </div>
              </div>
              
              <div className="text-xs text-stone-500 dark:text-stone-500">
                © {new Date().getFullYear()} FlightDeck. All rights reserved.
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  )
}
